import { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  useYAxisScale,
} from "recharts";
import type { BarShapeProps, TooltipContentProps } from "recharts";
import type { CompTopicDetailBucket, OverviewCompetitorChartRow } from "@nexus/contracts";
import { Button, cn, Icon } from "@nexus/ui";
import { BarSeriesGradientDefs } from "../OverviewChart/BarSeriesGradientDefs.js";
import { barGradientHoverId, barGradientId } from "../OverviewChart/chartBarGradients.js";
import { formatTopicTitle } from "../OverviewChart/chartPrimitives.js";
import { RoundedTopBar } from "../OverviewChart/RoundedTopBar.js";
import { downloadText } from "../../lib/download.js";
import { CHART_CSV_MIME } from "../../lib/exportChartCsv.js";
import {
  buildCompetitorChartCsv,
  competitorChartCsvFilename,
} from "../../lib/exportCompetitorChartCsv.js";
import { formatCompactNumber } from "../../lib/formatCompactNumber.js";
import {
  buildCompetitorChartData,
  metricKey,
  pctKey,
  type CompetitorChartSortDirection,
  type TopicDatum,
} from "./chartModel.js";

// Series colors: You + Main Competitor use design tokens; Benchmark is slate
// (#94A3B8) — no exact token exists, kept as a documented literal (per design).
const SERIES = [
  { key: "you", color: "var(--chart-1)" },
  { key: "mainCompetitor", color: "var(--chart-2)" },
  { key: "benchmark", color: "#94A3B8" },
] as const;

/** Gradient defs input — module scope so the defs' layout effect resolves stops once. */
const BAR_GRADIENT_SERIES = SERIES.map((s) => ({ id: s.key, colorVar: s.color }));

/** Fixed bar geometry, matching the My Brand chart's grouped bars. */
const BAR_SIZE = 40;
const BAR_GAP = 6;
const BAR_CATEGORY_GAP = "20%";

type SeriesDef = { key: CompTopicDetailBucket; label: string; color: string };

// Performance-metric dot colors — light bubble with dark text, mirroring the Retool
// chart. No exact tokens exist; documented literals (per design). A negative metric
// (only `Days/Assets` reaches below zero today) swaps green for the matching red tint.
const DOT_FILL = "#A7F3D0";
const DOT_TEXT = "#065F46";
const DOT_FILL_NEGATIVE = "#FECACA";
const DOT_TEXT_NEGATIVE = "#991B1B";

function dotColors(value: number): { fill: string; text: string } {
  return value < 0
    ? { fill: DOT_FILL_NEGATIVE, text: DOT_TEXT_NEGATIVE }
    : { fill: DOT_FILL, text: DOT_TEXT };
}

// How many topics fill the visible chart width; the rest are reached by horizontal
// scroll. Each topic occupies 1/VISIBLE_TOPICS of the visible area (pure CSS %).
const VISIBLE_TOPICS = 6;

const isRatioMetric = (metric: string) => metric === "Days/Assets";

// ─── X-axis label helpers ─────────────────────────────────────────────────────

const LABEL_MAX = 28;

function truncate(s: string, n = LABEL_MAX): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

interface AxisTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
}

/** Horizontal, de-prefixed, truncated topic tick; full name on hover when truncated. */
function CategoryTick({ x = 0, y = 0, payload }: AxisTickProps) {
  const full = formatTopicTitle(String(payload?.value ?? ""));
  const display = truncate(full);
  const isTruncated = display !== full;
  return (
    <g transform={`translate(${x},${y})`} pointerEvents={isTruncated ? "all" : "none"}>
      <text
        textAnchor="middle"
        dy={16}
        fontSize={11}
        fill="var(--muted-foreground)"
        style={isTruncated ? { cursor: "default" } : undefined}
      >
        {isTruncated ? <title>{full}</title> : null}
        {display}
      </text>
    </g>
  );
}

// ─── Performance-metric dot ───────────────────────────────────────────────────
// Rendered from a <LabelList> child of the Bar, so recharts hands us the bar's DODGED
// geometry ({ x, width } = this bucket's slot) and, through the list's valueAccessor, the
// metric off THIS bar's own row. Reading the row by the label `index` is wrong: recharts
// builds one label entry per bar it actually drew, so a bucket that skips a topic (no row,
// or a flat 0%) shifts every later index onto the wrong topic. The metric lives on the
// right axis → map its value to a pixel via useYAxisScale("right") (recharts 3 hook,
// valid inside the label slot, verified). The value is drawn inside the bubble, matching Retool.
interface MetricDotProps {
  /** This bar's row metric, resolved by the LabelList valueAccessor; null when absent. */
  value?: number | null;
  x?: number;
  width?: number;
}

function MetricDot({ value, x, width }: MetricDotProps) {
  const rightScale = useYAxisScale("right");
  if (!rightScale || x == null || width == null) return null;
  if (typeof value !== "number") return null; // null/absent → no dot (distinct from 0)
  const cy = rightScale(value) as unknown;
  if (typeof cy !== "number" || !Number.isFinite(cy)) return null;
  const cx = x + width / 2;
  const colors = dotColors(value);
  return (
    <g data-testid="metric-dot" pointerEvents="none">
      <circle cx={cx} cy={cy} r={16} fill={colors.fill} />
      <text
        x={cx}
        y={cy}
        dy={3}
        textAnchor="middle"
        fontSize={10}
        fill={colors.text}
      >
        {formatCompactNumber(value)}
      </text>
    </g>
  );
}

// ─── Tooltip (card-styled) ─────────────────────────────────────────────────────
// Six dataKeys feed the chart (${bucket}Pct bars + ${bucket}Metric lines); rather than
// matching a single dataKey, read the full datum off payload[0] and render Asset % +
// the metric per bucket.
function ChartTooltip({
  active,
  payload,
  label,
  series,
  metricLabel,
}: TooltipContentProps<number, string> & {
  series: SeriesDef[];
  metricLabel: string;
}) {
  if (!active || !payload?.length) return null;
  const datum = payload[0]?.payload as TopicDatum | undefined;
  if (!datum) return null;
  return (
    <div className="rounded-lg border bg-card p-3 text-xs shadow-md">
      <p className="mb-2 text-muted-foreground">
        {label != null ? formatTopicTitle(String(label)) : ""}
      </p>
      {series.map((s) => {
        const pct = datum[pctKey(s.key)];
        const metric = datum[metricKey(s.key)];
        if (pct == null && metric == null) return null;
        return (
          <div key={s.key} className="flex items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: s.color }}
            />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-medium text-foreground">
              {typeof pct === "number" ? `${Math.round(pct)}%` : "—"}
              {typeof metric === "number" ? ` · ${metric} ${metricLabel}` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface CompetitorComparisonChartProps {
  chartRows: OverviewCompetitorChartRow[];
  performanceMetric: string;
  youLabel: string;
  mainCompetitorLabel: string;
  benchmarkLabel: string;
  sortBucket?: CompTopicDetailBucket | null;
  sortDirection?: CompetitorChartSortDirection;
  onToggleSort?: (bucket: CompTopicDetailBucket) => void;
  onTopicBucketSelect?: (topicTitle: string, bucket: CompTopicDetailBucket) => void;
}

export function CompetitorComparisonChart({
  chartRows,
  performanceMetric,
  youLabel,
  mainCompetitorLabel,
  benchmarkLabel,
  sortBucket = null,
  sortDirection = "desc",
  onToggleSort,
  onTopicBucketSelect,
}: CompetitorComparisonChartProps) {
  const { t } = useTranslation();
  // Pivot per topic. Asset % (left axis) and the metric (right axis) are precomputed
  // server-side per bucket. A bucket with no row for a topic stays null (no data) — NOT
  // 0 (N6 guard: never let an absent bucket become `undefined * 100 = NaN`).
  const data = buildCompetitorChartData(chartRows, performanceMetric, sortBucket, sortDirection);

  const series: SeriesDef[] = SERIES.map((s) => ({
    key: s.key,
    color: s.color,
    label:
      s.key === "you"
        ? youLabel
        : s.key === "mainCompetitor"
          ? mainCompetitorLabel
          : benchmarkLabel,
  }));

  // Right-axis domain from the rendered metric values, padded, with 0 always in view
  // (Retool shows negatives below the baseline). Explicit so dots/labels aren't clipped.
  const metricValues = data.flatMap((d) =>
    series
      .map((s) => d[metricKey(s.key)])
      .filter((v): v is number => typeof v === "number"),
  );
  const minMetric = Math.min(0, ...metricValues);
  const maxMetric = Math.max(0, ...metricValues);
  const pad = (maxMetric - minMetric) * 0.18 || 1;
  const rightDomain: [number, number] = [minMetric - pad, maxMetric + pad];

  const axisTick = {
    fontSize: 11,
    fill: "var(--muted-foreground)",
  } as const;

  const chartInstanceId = useId().replace(/:/g, "");

  // Exports exactly what is plotted — same `data`/`series` the chart renders.
  const handleExport = useCallback(() => {
    downloadText(
      buildCompetitorChartCsv(data, series, performanceMetric),
      competitorChartCsvFilename(performanceMetric),
      CHART_CSV_MIME,
    );
  }, [data, series, performanceMetric]);

  const renderBar = ({ x, y, width, height, fill }: BarShapeProps) => (
    <RoundedTopBar
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      cursor={onTopicBucketSelect ? "pointer" : undefined}
    />
  );

  return (
    <div
      data-testid="competitor-chart"
      data-competitor-chart
      className="rounded-[14px] border border-border bg-card p-[21px]"
    >
      {/* Legend lives here, not in the chart: a Recharts <Legend /> renders inside the
        horizontally scrolling track and drifts out of view once topics exceed the
        visible width. */}
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-[15px] font-medium tracking-[-0.2px] text-foreground">
          Asset % vs {performanceMetric}
        </h3>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-1.5">
          <Button
            variant="ghost"
            size="sm"
            iconStart="download"
            onClick={handleExport}
            disabled={data.length === 0}
            data-testid="overview-competitor-export"
            className="h-7 shrink-0 rounded-[6px] px-2 py-1.5 text-[12px] text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-popover"
          >
            {t("overview.chart.export", "Export")}
          </Button>
          <div
            data-testid="competitor-chart-legend"
            className="flex shrink-0 flex-wrap items-center justify-end gap-x-1.5 gap-y-1.5"
          >
            {series.map((s) => {
              const isActiveSort = sortBucket === s.key;
              const effectiveSort = isActiveSort ? sortDirection : "desc";
              const sortLabel = t(
                effectiveSort === "desc" ? "overview.chart.sort.descending" : "overview.chart.sort.ascending",
                effectiveSort === "desc" ? "descending" : "ascending",
              );
              return (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`competitor-chart-legend-${s.key}`}
                  aria-pressed={isActiveSort}
                  aria-label={t("overview.chart.sort.metric", {
                    metric: s.label,
                    direction: sortLabel,
                    defaultValue: `Sort ${s.label} ${sortLabel}`,
                  })}
                  onClick={() => onToggleSort?.(s.key)}
                  className={cn(
                    "flex h-9 items-center gap-2 rounded-[10px] px-3 text-[13px] hover:bg-muted hover:text-foreground",
                    isActiveSort ? "bg-muted text-foreground" : "text-muted-foreground",
                  )}
                >
                  <span
                    className="size-3.5 shrink-0 rounded-[3px]"
                    style={{ background: s.color }}
                    aria-hidden
                  />
                  <span className="font-medium">{s.label}</span>
                  {isActiveSort ? (
                    <Icon
                      name={effectiveSort === "desc" ? "arrow-down" : "arrow-up"}
                      size="md"
                      color="inherit"
                      aria-hidden
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Suppressing the mousedown default keeps a bar click from focusing the recharts
        surface or a bar group, which the base `outline-ring` rule would then ring. See
        the fuller note in OverviewChart/views/BarsView.tsx. */}
      <div
        className="mt-4 h-[420px] w-full overflow-x-auto overflow-y-hidden"
        onMouseDown={(event) => event.preventDefault()}
      >
        {data.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
            No comparison data for the current filters.
          </div>
        ) : (
          // Percentage width resolves against the scroll container's visible width V:
          // the track is V·N/6 px so each of N topics gets V/6 (≈6 in view); ≤6 topics
          // clamp to 100% (fills, no scroll). ResponsiveContainer then measures definite px.
          <div
            data-testid="chart-scroll-track"
            className="h-full"
            style={{
              width: `${Math.max(100, (data.length / VISIBLE_TOPICS) * 100)}%`,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 24, right: 24, bottom: 8, left: 8 }}
                barGap={BAR_GAP}
                barCategoryGap={BAR_CATEGORY_GAP}
              >
                <BarSeriesGradientDefs
                  chartInstanceId={chartInstanceId}
                  metrics={BAR_GRADIENT_SERIES}
                />
                <CartesianGrid
                  yAxisId="left"
                  syncWithTicks
                  stroke="var(--border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="topic"
                  interval={0}
                  height={40}
                  tick={<CategoryTick />}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `${Math.round(v)}%`}
                />
                {/* Right axis scales the metric dots — its sole consumer is MetricDot
                  via useYAxisScale("right"). */}
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={rightDomain}
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  {...(isRatioMetric(performanceMetric)
                    ? { tickFormatter: (v: number) => `${Math.round(v)}%` }
                    : {})}
                />
                <Tooltip
                  cursor={false}
                  content={(props) => (
                    <ChartTooltip
                      {...(props as TooltipContentProps<number, string>)}
                      series={series}
                      metricLabel={performanceMetric}
                    />
                  )}
                />
                {/* Asset % bars (left axis); each bar's label list draws this bucket's metric dot. */}
                {series.map((s) => (
                  <Bar
                    key={s.key}
                    yAxisId="left"
                    dataKey={pctKey(s.key)}
                    name={s.label}
                    fill={`url(#${barGradientId(chartInstanceId, s.key)})`}
                    shape={renderBar}
                    activeBar={(props) =>
                      renderBar({
                        ...props,
                        fill: `url(#${barGradientHoverId(chartInstanceId, s.key)})`,
                      })
                    }
                    barSize={BAR_SIZE}
                    isAnimationActive={false}
                    onClick={(barData) => {
                      const row = barData?.payload as TopicDatum | undefined;
                      if (row?.topic && onTopicBucketSelect) {
                        onTopicBucketSelect(row.topic, s.key);
                      }
                    }}
                  >
                    <LabelList
                      content={<MetricDot />}
                      valueAccessor={(entry) =>
                        (entry.payload as TopicDatum | undefined)?.[metricKey(s.key)] ?? null
                      }
                    />
                  </Bar>
                ))}

                {/* Transparent anchors register the right axis + bind it to the metric
                  data so useYAxisScale("right") resolves. Hidden from chart + legend. */}
                {series.map((s) => (
                  <Line
                    key={`anchor-${s.key}`}
                    yAxisId="right"
                    dataKey={metricKey(s.key)}
                    stroke="transparent"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
